import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Users, User, Crown, Shield, Search, Trash2, Edit, X, Save, X as XIcon } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import FormDrawer from './FormDrawer';
import { toUTCPlus5 } from '../utils/time';
import { useNavigate } from 'react-router-dom';

const UserListContent = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    department: '',
    role: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Filter state
  const [selectedName, setSelectedName] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedUsername, setSelectedUsername] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Fetch regular users (members and project leads)
        const res = await axios.get('/api/users/all');
        setUsers(res.data);
        
        // Fetch users including admins for team lead dropdown
        const resWithAdmins = await axios.get('/api/users/all?include_admins=true');
        const adminUsers = resWithAdmins.data.filter(u => u.role === 'admin');
        // Remove duplicates and sort by name
        const uniqueAdmins = adminUsers.filter((admin, index, self) => 
          index === self.findIndex(a => a.id === admin.id)
        ).sort((a, b) => (a.full_name || a.username || '').localeCompare(b.full_name || b.username || ''));
        setTeamLeads(uniqueAdmins);
      } catch (err) {
        console.error('Error fetching users:', err);
        // handle error
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleDelete = async (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(prev => ({ ...prev, [userToDelete.id]: true }));
    try {
      await axios.delete(`/api/users/${userToDelete.id}`);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
    } catch (err) {
      // handle error
    } finally {
      setDeleting(prev => ({ ...prev, [userToDelete.id]: false }));
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      department: user.department || '',
      role: user.role || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      await axios.patch(`/api/users/${editingUser.id}`, formData);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...formData } : u));
      setEditingUser(null);
      setShowEditModal(false);
      setFormData({
        department: '',
        role: ''
      });
    } catch (error) {
      // handle error
    }
  };

  // Enhanced filtering logic - combine users and teamLeads for filtering
  const allUsersForFiltering = [...users, ...teamLeads];
  
  const filteredUsers = allUsersForFiltering.filter(user => {
    // Apply individual column filters first
    if (selectedName && !user.full_name?.toLowerCase().includes(selectedName.toLowerCase())) {
      return false;
    }
    
    if (selectedEmail && !user.email?.toLowerCase().includes(selectedEmail.toLowerCase())) {
      return false;
    }
    
    if (selectedUsername && !user.username?.toLowerCase().includes(selectedUsername.toLowerCase())) {
      return false;
    }
    
    if (selectedDepartment !== 'all' && user.department !== selectedDepartment) {
      return false;
    }
    
    if (selectedRole !== 'all' && user.role !== selectedRole) {
      return false;
    }
    
    // Then apply search term filtering across all columns
    if (!search) return true;
    
    const searchLower = search.toLowerCase();
    
    // Search in all relevant fields
    return (
      // Basic info
      (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.username && user.username.toLowerCase().includes(searchLower)) ||
      
      // Role and department
      (user.role && user.role.toLowerCase().includes(searchLower)) ||
      (user.department && user.department.toLowerCase().includes(searchLower)) ||
      
      // Additional fields that might exist
      (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
      (user.location && user.location.toLowerCase().includes(searchLower)) ||
      (user.skills && Array.isArray(user.skills) && user.skills.some(skill => skill.toLowerCase().includes(searchLower))) ||
      (user.tags && Array.isArray(user.tags) && user.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedName, selectedEmail, selectedUsername, selectedDepartment, selectedRole]);

  // Show counts from all users (including admins from teamLeads)
  const totalAdmins = allUsersForFiltering.filter(u => u.role === 'admin').length;
  const totalProjectLeads = allUsersForFiltering.filter(u => u.role === 'project_lead').length;
  const totalMembers = allUsersForFiltering.filter(u => u.role === 'member').length;

  // Unique roles for dropdown
  const uniqueRoles = ['admin', 'project_lead', 'member'];
  // Team leads for dropdown (fetched separately from /api/users/team-leads)
  const adminUsers = teamLeads;


  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="text-2xl font-bold text-secondary-900">All Users</h1>
      </div>

      {/* Search Section */}
      <div className="card">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search across all columns..."
              className="input-field pl-10"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="text-secondary-400 hover:text-secondary-600"
              title="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
          {(search || selectedName || selectedEmail || selectedUsername || selectedDepartment !== 'all' || selectedRole !== 'all') && (
            <button
              onClick={() => {
                setSearch('');
                setSelectedName('');
                setSelectedEmail('');
                setSelectedUsername('');
                setSelectedDepartment('all');
                setSelectedRole('all');
                setCurrentPage(1);
              }}
              className="btn-secondary text-sm"
              title="Clear all filters and search"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Results summary */}
        <div className="mt-4 text-sm text-secondary-600">
          Showing {filteredUsers.length} of {allUsersForFiltering.length} users
        </div>
      </div>

              {/* Filter Section */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Name
              </label>
              <input
                type="text"
                className="input-field w-full"
                placeholder="Filter by name..."
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Email
              </label>
              <input
                type="text"
                className="input-field w-full"
                placeholder="Filter by email..."
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Username
              </label>
              <input
                type="text"
                className="input-field w-full"
                placeholder="Filter by username..."
                value={selectedUsername}
                onChange={(e) => setSelectedUsername(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Department
              </label>
              <select
                className="input-field w-full"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {Array.from(new Set(allUsersForFiltering.map(u => u.department).filter(Boolean))).map(dept => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Role
              </label>
              <select
                className="input-field w-full"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="project_lead">Project Lead</option>
                <option value="member">Member</option>
              </select>
            </div>
          </div>
          
          {/* Clear Filters Button */}
          {(selectedName || selectedEmail || selectedUsername || selectedDepartment !== 'all' || selectedRole !== 'all') && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setSelectedName('');
                  setSelectedEmail('');
                  setSelectedUsername('');
                  setSelectedDepartment('all');
                  setSelectedRole('all');
                }}
                className="btn-secondary text-sm"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>

      {/* Summary Cards */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 flex items-center justify-center gap-2"><Users className="h-6 w-6" />{users.length + teamLeads.length}</div>
            <div className="text-sm text-secondary-600">Total Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600 flex items-center justify-center gap-2"><Crown className="h-6 w-6" />{totalAdmins}</div>
            <div className="text-sm text-secondary-600">Admins</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 flex items-center justify-center gap-2"><Shield className="h-6 w-6" />{totalProjectLeads}</div>
            <div className="text-sm text-secondary-600">Project Leads</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary-600 flex items-center justify-center gap-2"><User className="h-6 w-6" />{totalMembers}</div>
            <div className="text-sm text-secondary-600">Members</div>
          </div>
        </div>
      </div>

      

      {/* Users — desktop table / mobile cards */}
      <div className="card !p-0 sm:!p-6 overflow-hidden">
        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-700">
          {loading ? (
            <div className="text-center py-12 text-secondary-400">Loading...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-secondary-400 px-4">
              {users.length === 0 ? 'No users found.' : 'No users match your search'}
            </div>
          ) : (
            paginatedUsers.map((user) => (
              <div key={user.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${user.id}`)}
                    className="text-left min-w-0 flex-1"
                  >
                    <div className="font-semibold text-secondary-900 dark:text-white truncate hover:text-primary-600">
                      {user.full_name}
                    </div>
                    <div className="text-sm text-secondary-500 break-all">{user.email}</div>
                    <div className="text-xs text-secondary-400 mt-0.5">@{user.username}</div>
                  </button>
                  <span className={`shrink-0 inline-block px-2 py-1 rounded-full text-xs font-semibold capitalize ${user.role === 'admin' ? 'bg-yellow-100 text-yellow-800' : user.role === 'project_lead' ? 'bg-blue-100 text-blue-800' : 'bg-secondary-100 text-secondary-600'}`}>
                    {user.role === 'project_lead' ? 'Project Lead' : user.role}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
                  <div><span className="text-secondary-400">Dept:</span> {user.department || '—'}</div>
                  <div>
                    <span className="text-secondary-400">2FA:</span>{' '}
                    <span className={user.two_factor_enabled ? 'text-green-600' : 'text-red-600'}>
                      {user.two_factor_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-400">GitHub:</span>{' '}
                    <span className={user.github_connected ? 'text-green-600' : 'text-red-600'}>
                      {user.github_connected ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-400">Calendar:</span>{' '}
                    <span className={user.microsoft_calendar_connected ? 'text-green-600' : 'text-red-600'}>
                      {user.microsoft_calendar_connected ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-400">Spotify:</span>{' '}
                    <span className={user.spotify_connected ? 'text-green-600' : 'text-red-600'}>
                      {user.spotify_connected ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-secondary-400">
                    Last login:{' '}
                    {user.last_login
                      ? (() => {
                          const date = new Date(user.last_login);
                          const pakistanTime = new Date(date.getTime() + 5 * 60 * 60 * 1000);
                          return pakistanTime.toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          });
                        })()
                      : 'Never'}
                  </span>
                  <div className="flex gap-1">
                    <button
                      className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-400 hover:text-primary-600"
                      title="Edit User"
                      onClick={() => handleEdit(user)}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-400 hover:text-red-600"
                      title="Delete User"
                      disabled={deleting[user.id]}
                      onClick={() => handleDelete(user)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-100 text-sm">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Name</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Username</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Department</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Role</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider whitespace-nowrap">
                  Last Login
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">2FA Enabled</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">GitHub Integrated</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Outlook Calendar</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Spotify Connected</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Meeting Time Tracking</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {loading ? (
                <tr><td colSpan={12} className="text-center py-12 text-secondary-400">Loading...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-12 text-secondary-400">
                  {users.length === 0 ? 'No users found.' : 'No users match your search'}
                </td></tr>
              ) : (
                paginatedUsers.map(user => (
                  <tr key={user.id} className="hover:bg-primary-50 transition">
                    <td className="px-4 py-2 text-sm font-medium text-secondary-900">
                      <button
                        onClick={() => navigate(`/users/${user.id}`)}
                        className="text-left cursor-pointer hover:text-primary-600 transition-colors"
                      >
                        <span className="block leading-tight">{user.full_name}</span>
                      </button>
                    </td>
                    <td className="px-4 py-2">{user.email}</td>
                    <td className="px-4 py-2">{user.username}</td>
                    <td className="px-4 py-2">{user.department || '-'}</td>
                    <td className="px-4 py-2 capitalize">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.role === 'admin' ? 'bg-yellow-100 text-yellow-800' : user.role === 'project_lead' ? 'bg-blue-100 text-blue-800' : 'bg-secondary-100 text-secondary-600'}`}>{user.role === 'project_lead' ? 'Project Lead' : user.role}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-secondary-600">
                                              {user.last_login ? (
                          <span 
                            title={`${toUTCPlus5(user.last_login)}`}
                            className="whitespace-nowrap block"
                          >
                            {(() => {
                              const date = new Date(user.last_login);
                              const pakistanTime = new Date(date.getTime() + (5 * 60 * 60 * 1000));
                              return pakistanTime.toLocaleString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              });
                            })()}
                          </span>
                        ) : (
                          <span className="text-secondary-400 italic">Never</span>
                        )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.two_factor_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.two_factor_enabled ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.github_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.github_connected ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.microsoft_calendar_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.microsoft_calendar_connected ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.spotify_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                        data-testid="user-spotify-connected"
                      >
                        {user.spotify_connected ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {user.role === 'admin' ? (
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                          N/A
                        </span>
                      ) : user.microsoft_calendar_connected ? (
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${user.meeting_time_tracking_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {user.meeting_time_tracking_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                          Not Available
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 flex gap-2">
                      <button
                        className="p-1 text-secondary-400 hover:text-primary-600 transition-colors"
                        title="Edit User"
                        onClick={() => handleEdit(user)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="p-1 text-secondary-400 hover:text-red-600 transition-colors"
                        title="Delete User"
                        disabled={deleting[user.id]}
                        onClick={() => handleDelete(user)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

       
        {/* Pagination and Page Size */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 sm:p-0 sm:mt-6">
          {/* Page size selector - always visible */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary-600">Show:</span>
            <select
              className="input-field w-20 text-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-secondary-600">per page</span>
          </div>

          {/* Pagination controls - only show when multiple pages */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button 
                className="btn-secondary px-3" 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  className={`px-3 py-1 rounded transition-colors ${
                    currentPage === i + 1 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="btn-secondary px-3" 
                disabled={currentPage === totalPages} 
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
          
          {/* Page info */}
          <div className="text-sm text-secondary-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>

        {/* Edit User Drawer */}
        <FormDrawer
          open={showEditModal}
          onClose={() => { setEditingUser(null); setShowEditModal(false); }}
          title="Edit User"
          subtitle="Update this user's department and role."
          icon={<Edit className="h-5 w-5" />}
          iconColor="primary"
          widthClass="sm:max-w-md lg:max-w-lg"
          footer={(
            <>
              <button
                type="button"
                onClick={() => { setEditingUser(null); setShowEditModal(false); }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Update
              </button>
            </>
          )}
        >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Role
                  </label>
                  <select
                    className="input-field"
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                  >
                    {uniqueRoles.map(role => (
                      <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                    ))}
                  </select>
                </div>

              </div>
        </FormDrawer>

        {/* Confirm Delete Modal */}
        <ConfirmModal
          open={showDeleteModal}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete?.full_name || userToDelete?.email}? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          confirmText="Delete"
          cancelText="Cancel"
        />
      </div>
    </div>
  );
};

const UserList = () => <UserListContent />;
export default UserList;